import * as yup from "yup";
import { FormikProps } from "formik";
import Grid from "@material-ui/core/Grid";
import QuestionnaireScreen, { deserializeResponses, serializeResponses } from "../../QuestionnaireScreen";
import { Body } from "../../GridLayout";
import Select from "../../Select";
import { Values } from "../../ControlledForm";
import { SubtitleText, TitleText } from "../../../controls/helpers";
import { riskOptions, withdrawTimeframes, YesNoOption } from "../../../../common/types";
import { useAppSelector } from "../../../../store";

const planToWithdrawQuestionName = "planToWithdraw";
const planToWithdrawQuestion = "Are you planning on withdrawing your KiwiSaver in the next 5 years?";
const timeFrameQuestionName = "timeframe";
const timeFrameQuestion = "Approximately when do you plan to make a withdrawal?";
const employmentQuestionName = "employmentStatus";
const employmentQuestion = "Employment Status";
const riskProfileQuestionName = "riskProfile";
const riskProfileQuestion = "How would you feel if your investment balance dropped 30%?";
const feesQuestionName = "feesVersusReturns";
const feesQuestion = "Which best describes you?";
const feesOptions = {
  high: "For higher potential returns, I am willing to pay higher fees",
  low: `For lower potential returns, I'd rather pay lower fees`,
};

const questions = {
  [planToWithdrawQuestionName]: planToWithdrawQuestion,
  [timeFrameQuestionName]: timeFrameQuestion,
  [employmentQuestionName]: employmentQuestion,
  [riskProfileQuestionName]: riskProfileQuestion,
  [feesQuestionName]: feesQuestion,
};

export const useRiskProfile = () => {
  const { responses } = useAppSelector((state) => state.clientQuestionnaires);
  const { riskProfile } = deserializeResponses({ [riskProfileQuestionName]: riskProfileQuestion })(responses || {});
  return (riskProfile || riskOptions.low) as string;
};

export const usePlanningToWithdraw = () => {
  const { responses } = useAppSelector((state) => state.clientQuestionnaires);
  const { planToWithdraw } = deserializeResponses(questions)(responses || {});
  return planToWithdraw === YesNoOption.Yes;
};

export const useWithdrawTimeframe = () => {
  const { responses } = useAppSelector((state) => state.clientQuestionnaires);
  const { timeframe } = deserializeResponses(questions)(responses || {});
  return (timeframe || "") as string;
};

export const useEmployed = () => {
  const { responses } = useAppSelector((state) => state.clientQuestionnaires);
  const { employmentStatus } = deserializeResponses({ [employmentQuestionName]: employmentQuestion })(responses || {});
  return employmentStatus === "Employed" || !employmentStatus;
};

const BeginQuestions = () => {
  const _planToWithdrawQuestion = questions[planToWithdrawQuestionName];
  const _employmentQuestion = questions[employmentQuestionName];
  const _riskProfileQuestion = questions[riskProfileQuestionName];
  const _feesQuestion = questions[feesQuestionName];

  const serializeWithdrawResponses = (values: Values): Record<string, string> => {
    const responses = serializeResponses(questions)(values);

    // Make sure we don't send response to withdraw timeframe question if planToWithdraw === 'No'
    if (responses[_planToWithdrawQuestion] && responses[_planToWithdrawQuestion] === YesNoOption.No)
      return {
        [_planToWithdrawQuestion]: responses[_planToWithdrawQuestion],
        [_employmentQuestion]: responses[_employmentQuestion],
        [_riskProfileQuestion]: responses[_riskProfileQuestion],
        [_feesQuestion]: responses[_feesQuestion],
      };
    else return responses;
  };

  return (
    <QuestionnaireScreen
      initialValues={{
        [employmentQuestionName]: "",
        [planToWithdrawQuestionName]: "",
        [timeFrameQuestionName]: "",
        [riskProfileQuestionName]: "",
        [feesQuestionName]: "",
      }}
      validationSchema={validationSchema}
      serializeResponses={serializeWithdrawResponses}
      deserializeResponses={deserializeResponses(questions)}
      overrideNextPageRoute={(values) => (values.planToWithdraw === YesNoOption.Yes ? "ks-self-select" : "ks-life-cycle")}
    >
      {({ values }: FormikProps<any>) => (
        <Body isNarrowView>
          <TitleText>Here are a few questions so we can help you maximise your retirement savings.</TitleText>
          <Grid container spacing={3} alignItems="center">
            <Grid item sm={2} xs={12}>
              <SubtitleText hideMarginBottom>Are you…</SubtitleText>
            </Grid>
            <Grid item sm="auto" xs={12}>
              <Select noBlankOption name={employmentQuestionName} options={["Employed", "Self-employed", "Other"]} />
            </Grid>
          </Grid>
          <Grid container spacing={3} alignItems="center" style={{ marginTop: 24 }}>
            <Grid item sm={9} xs={12}>
              <SubtitleText hideMarginBottom>{_planToWithdrawQuestion} E.g. to buy your first home or for retirement.</SubtitleText>
            </Grid>
            <Grid item sm={3} xs={12}>
              <Select noBlankOption name={planToWithdrawQuestionName} options={[YesNoOption.Yes, YesNoOption.No]} />
            </Grid>
          </Grid>
          {values[planToWithdrawQuestionName] === YesNoOption.Yes && (
            <Grid container spacing={3} alignItems="center" style={{ marginTop: 24 }}>
              <Grid item sm={8} xs={12}>
                <SubtitleText hideMarginBottom>{timeFrameQuestion}</SubtitleText>
              </Grid>
              <Grid item sm={4} xs={12}>
                <Select noBlankOption name={timeFrameQuestionName} options={withdrawTimeframes.map((t) => t.label)} />
              </Grid>
            </Grid>
          )}
          <Grid container spacing={3} alignItems="center" style={{ marginTop: 24 }}>
            <Grid item sm={6} xs={12}>
              <SubtitleText hideMarginBottom>{riskProfileQuestion}</SubtitleText>
            </Grid>
            <Grid item sm={6} xs={12}>
              <Select noBlankOption name={riskProfileQuestionName} options={Object.values(riskOptions)} />
            </Grid>
          </Grid>
          <Grid container spacing={3} alignItems="center" style={{ marginTop: 24 }}>
            <Grid item sm={3} xs={12}>
              <SubtitleText hideMarginBottom>{feesQuestion}</SubtitleText>
            </Grid>
            <Grid item sm={9} xs={12}>
              <Select noBlankOption name={feesQuestionName} options={Object.values(feesOptions)} />
            </Grid>
          </Grid>
        </Body>
      )}
    </QuestionnaireScreen>
  );
};

const validationSchema = yup.object().shape({
  [planToWithdrawQuestionName]: yup.string().required("A response is required"),
  [timeFrameQuestionName]: yup.string().when([planToWithdrawQuestionName], {
    is: (mainQuestionResponse: string) => mainQuestionResponse === YesNoOption.Yes,
    then: yup.string().required("A response is required"),
    otherwise: yup.string(),
  }),
  [employmentQuestionName]: yup.string().required(),
  [riskProfileQuestionName]: yup.string().required("A response is required"),
  [feesQuestionName]: yup.string().required("A response is required"),
});

export default BeginQuestions;
